import React from 'react';
import { motion } from 'motion/react';
import { Heart, Shield, Bell, Cloud, ArrowRight, UserPlus, User, Zap } from 'lucide-react';
import { useApp } from '../context';

interface WelcomeProps {
  onComplete: () => void;
  onLogin: () => void;
  onShowLegal: (page: 'privacy' | 'terms') => void;
}

export default function Welcome({ onComplete, onLogin, onShowLegal }: WelcomeProps) {
  const { setUser } = useApp();

  const handleGuest = () => {
    setUser({
      name: 'Guest User',
      email: '',
      isGuest: true,
    });
    onComplete();
  };

  const handleCreateAccount = () => {
    onLogin();
  };

  const benefits = [
    { icon: Cloud, label: 'Cloud Sync', desc: 'Access your contacts on any device' },
    { icon: Bell, label: 'Smart Notifications', desc: 'Never miss a birthday or milestone' },
    { icon: Zap, label: 'Totally Free', desc: 'No hidden costs, no subscriptions' },
    { icon: Shield, label: 'Privacy First', desc: 'Your data is encrypted and never shared' },
  ];

  return (
    <div className="h-[100dvh] bg-[#f8f5f5] flex flex-col max-w-md mx-auto shadow-2xl overflow-hidden relative">
      {/* Background Accents */}
      <div className="absolute top-12 -left-24 size-64 bg-[#fa8585]/10 rounded-full blur-3xl"></div>
      <div className="absolute top-1/2 -right-24 size-64 bg-blue-500/10 rounded-full blur-3xl"></div>

      <div className="flex-1 flex flex-col p-6 relative z-10 overflow-hidden">
        {/* Logo Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col items-center mt-2 mb-3"
        >
          <div className="size-14 rounded-[1.75rem] bg-[#fa8585] flex items-center justify-center text-white shadow-2xl shadow-[#fa8585]/40 mb-3">
            <Heart size={28} fill="white" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Nudge</h1>
          <p className="text-[13px] text-slate-500 font-medium mt-1.5 text-center px-4 leading-relaxed">
            The thoughtful way to stay connected with those who matter most.
          </p>
        </motion.div>

        <div className="space-y-2.5 mb-2 relative z-10">
          {benefits.map((benefit, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 + idx * 0.1 }}
              className="bg-white p-3.5 rounded-[20px] shadow-sm border border-slate-100 flex items-center gap-3.5"
            >
              <div className="size-10 rounded-xl bg-slate-50 text-[#fa8585] flex items-center justify-center shrink-0">
                <benefit.icon size={20} />
              </div>
              <div>
                <p className="font-bold text-slate-900 text-[13px]">{benefit.label}</p>
                <p className="text-[11px] text-slate-400 mt-0.5">{benefit.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="mt-auto space-y-3 pt-2 pb-10 relative z-10 flex flex-col justify-end shrink-0">
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            onClick={handleCreateAccount}
            className="w-full bg-slate-900 text-white py-4 rounded-[2rem] font-bold flex items-center justify-center gap-3 shadow-xl hover:scale-[1.02] active:scale-95 transition-transform"
          >
            <UserPlus size={20} />
            Sign In / Create Account
            <ArrowRight size={18} className="ml-2" />
          </motion.button>

          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            onClick={handleGuest}
            className="w-full bg-white text-slate-600 py-4 rounded-[2rem] font-bold flex items-center justify-center gap-3 border border-slate-200 hover:bg-slate-50 active:scale-95 transition-transform"
          >
            <User size={20} />
            Continue as Guest
          </motion.button>

          <p className="text-center text-[10px] text-slate-400 font-bold tracking-widest mt-4">
            By continuing, you agree to our{' '}
            <button
              onClick={() => onShowLegal('terms')}
              className="underline text-slate-500 hover:text-slate-700"
            >
              Terms of Service
            </button>{' '}
            and{' '}
            <button
              onClick={() => onShowLegal('privacy')}
              className="underline text-slate-500 hover:text-slate-700"
            >
              Privacy Policy
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
